import React, { useMemo, useState, useEffect } from 'react';
import { Rocket, Package, Box, Users, TrendingUp, CheckCircle, X, ChevronRight, BookOpen } from 'lucide-react';
import onboardingData from '../../help/onboarding/onboarding.json';
import { closeButtonStyle, modalOverlayStyle } from '../../styles/formStyles';
import { isFeatureEnabled } from '../../utils/featureFlagsStore';

const iconMap: Record<string, React.ReactNode> = {
  rocket: <Rocket size={32} color="#3b82f6" />,
  package: <Package size={32} color="#3b82f6" />,
  box: <Box size={32} color="#3b82f6" />,
  users: <Users size={32} color="#3b82f6" />,
  'trending-up': <TrendingUp size={32} color="#3b82f6" />,
  'check-circle': <CheckCircle size={32} color="#10b981" />,
  book: <BookOpen size={32} color="#3b82f6" />,
};

interface OnboardingModalProps {
  onComplete: () => void;
  onNavigate?: (page: string) => void;
}

const STORAGE_KEY = 'mms_onboarding_complete';

export function isOnboardingComplete(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function markOnboardingComplete() {
  localStorage.setItem(STORAGE_KEY, 'true');
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ onComplete, onNavigate }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const steps = useMemo(
    () =>
      onboardingData.steps.filter((s: { requiredFeatures?: string[] }) => {
        const required = s.requiredFeatures;
        if (!required?.length) return true;
        return required.every((code) => isFeatureEnabled(code));
      }),
    [],
  );
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleNext = () => {
    if (isLast) {
      markOnboardingComplete();
      onComplete();
    } else {
      // If step has an action, optionally navigate
      const nextStep = steps[stepIndex + 1];
      if (nextStep?.action && onNavigate) {
        // We don't actually navigate during onboarding — just advance
      }
      setStepIndex(i => i + 1);
    }
  };

  const handleSkip = () => {
    markOnboardingComplete();
    onComplete();
  };

  return (
    <div style={{ ...modalOverlayStyle, zIndex: 10000 }}>
      <div style={{
        width: '480px', maxWidth: '90vw', backgroundColor: 'white', borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0' }}>
          <button type="button" onClick={handleSkip} title="Close" style={closeButtonStyle}>
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '0 40px 32px', textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            {iconMap[step.icon || 'rocket'] || iconMap.rocket}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
            {step.title}
          </h2>
          <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: 1.6, marginBottom: '32px' }}>
            {step.description}
          </p>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '28px' }}>
            {steps.map((_, i) => (
              <div key={i} style={{
                width: i === stepIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                backgroundColor: i === stepIndex ? '#3b82f6' : i < stepIndex ? '#93c5fd' : '#E5E7EB',
                transition: 'all 0.3s',
              }} />
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            {!isLast && (
              <button
                onClick={handleSkip}
                style={{
                  padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280',
                  border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
                }}
              >
                Skip
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: '10px 24px', backgroundColor: '#3b82f6', color: 'white',
                border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
